import { isAbortLikeMessage } from './lib/requestAbort';

import { createRoot } from 'react-dom/client';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  ArrowDown,
  ArrowUp,
  Bold,
  ChevronDown,
  Edit2,
  Image as ImageIcon,
  Italic,
  Link,
  List,
  ListOrdered,
  Redo,
  Strikethrough,
  Underline,
  Undo,
} from 'lucide-react';
import App from './App.tsx';
import RuntimeTenantGate from './components/app/RuntimeTenantGate';
import ErrorBoundary from './components/ui/ErrorBoundary';
import { DisplayTimezoneProvider } from './components/app/DisplayTimezoneProvider';
import PublicSiteShell from './components/public/PublicSiteShell';
import { normalizePublicPath, type PublicRoutePath } from './components/public/publicRoutes';
import './index.css';
import { attachDebugHelpers } from './dev/tickSizeValidator';
import { maybeHandleSnapTradeCallback } from './snaptradeCallback';
import { currentRuntime } from './config/runtime';
import { applyNeutralRuntimeMetadata, applyRuntimeMetadata } from './config/runtimeMetadata';
import { isBaseTradeProofHostname, isTradeProofMarketingOnlyHostname, WHITE_LABEL_BASE_DOMAIN } from './lib/whiteLabel';

(globalThis as any).TrendingUp = ArrowUp;
(globalThis as any).TrendingDown = ArrowDown;
(globalThis as any).Edit2 = Edit2;
(globalThis as any).Bold = Bold;
(globalThis as any).Italic = Italic;
(globalThis as any).Underline = Underline;
(globalThis as any).Strikethrough = Strikethrough;
(globalThis as any).Undo = Undo;
(globalThis as any).Redo = Redo;
(globalThis as any).Link = Link;
(globalThis as any).ImageIcon = ImageIcon;
(globalThis as any).AlignLeft = AlignLeft;
(globalThis as any).AlignCenter = AlignCenter;
(globalThis as any).AlignRight = AlignRight;
(globalThis as any).List = List;
(globalThis as any).ListOrdered = ListOrdered;
(globalThis as any).ChevronDown = ChevronDown;
try {
  window.eval?.('var TrendingUp = window.TrendingUp; var TrendingDown = window.TrendingDown; var Edit2 = window.Edit2; var Bold = window.Bold; var Italic = window.Italic; var Underline = window.Underline; var Strikethrough = window.Strikethrough; var Undo = window.Undo; var Redo = window.Redo; var Link = window.Link; var ImageIcon = window.ImageIcon; var AlignLeft = window.AlignLeft; var AlignCenter = window.AlignCenter; var AlignRight = window.AlignRight; var List = window.List; var ListOrdered = window.ListOrdered; var ChevronDown = window.ChevronDown;');
} catch {}

// Apply tenant-aware metadata (title, favicon, manifest, og tags) early during boot.
// Tenant hostnames (wildcards and custom domains) must never start with the base
// brand: they boot neutral so the public branding payload can install the tenant's
// own title/favicon without ever flashing the default Trade-Proof assets.
try {
  const bootHostname = String(window.location.hostname || '').trim().toLowerCase();
  if (isBaseTradeProofHostname(bootHostname)) {
    applyRuntimeMetadata(currentRuntime);
  } else {
    applyNeutralRuntimeMetadata();
  }
} catch {}

const rootEl = document.getElementById('root');
const BOOT_FAILURE_TIMEOUT_MS = Number(import.meta.env?.VITE_BOOT_FAILURE_TIMEOUT_MS || 15000);

const BOOT_FALLBACK_SUPPRESSED_PATHS = new Set([
  '/',
  '/help',
  '/legal',
  '/security',
  '/feature-request',
  '/auth/callback',
  '/auth/reset-password',
  '/forgot-password',
  '/help/getting-started',
  '/help/importing-trades',
  '/help/broker-support',
  '/help/replay-and-review',
  '/help/billing-and-trial',
  '/help/account-settings',
  '/login',
  '/signup',
  '/terms',
  '/terms-of-service',
  '/privacy',
  '/privacy-policy',
]);

const readNormalizedPathname = () => {
  try {
    const pathname = String(window.location.pathname || '/').trim() || '/';
    return pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  } catch {
    return '/';
  }
};

const shouldSuppressBootFallback = () => BOOT_FALLBACK_SUPPRESSED_PATHS.has(readNormalizedPathname());

const showBootError = (message: string, stack?: string) => {
  try {
    if (shouldSuppressBootFallback()) return;
    // The technical details are for logs/monitoring only — never shown to users.
    try { console.error('[boot] workspace initialization failed', message, stack || ''); } catch {}
    if (rootEl) {
      rootEl.innerHTML = `
        <div style="font-family: system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif; padding: 24px; color: #e2e8f0; background: #0b1220; min-height: 100vh;">
          <div style="font-weight: 600; font-size: 18px; color: #f8fafc;">Something went wrong while loading your workspace.</div>
          <div style="margin-top: 8px; color: #cbd5e1; font-size: 14px; white-space: pre-line;">Please refresh the page.
If the problem continues, contact support.</div>
          <div style="margin-top: 16px; display: flex; gap: 12px; align-items: center;">
            <button onclick="window.location.reload()" style="border: none; border-radius: 8px; padding: 10px 14px; font-size: 13px; font-weight: 600; background: #2563eb; color: #fff; cursor: pointer;">Refresh</button>
            <a href="/help#contact" style="border: 1px solid rgba(148,163,184,0.45); border-radius: 8px; padding: 10px 14px; font-size: 13px; font-weight: 600; color: #e2e8f0; text-decoration: none;">Contact Support</a>
          </div>
        </div>
      `;
    }
  } catch (e) {
    try { console.error('showBootError failed', e); } catch {}
  }
};

try {
  let bootSettled = false;
  let bootFailureTimer: number | null = null;

  const markBootSettled = () => {
    bootSettled = true;
    if (bootFailureTimer != null) {
      window.clearTimeout(bootFailureTimer);
      bootFailureTimer = null;
    }
  };

  const isIgnorableBrowserNoise = (message: string) => {
    const msg = String(message || '');
    return msg.includes('ResizeObserver loop completed with undelivered notifications')
      || msg.includes('ResizeObserver loop limit exceeded')
      || isAbortLikeMessage(msg);
  };

  window.addEventListener('error', (event) => {
    if (bootSettled) return;
    const msg = event?.message || 'Unknown error';
    if (isIgnorableBrowserNoise(msg)) return;
    const stack = (event && (event as any).error && (event as any).error.stack) || (event as any).filename + ':' + (event as any).lineno;
    showBootError(msg, stack);
  });
  window.addEventListener('unhandledrejection', (event) => {
    if (bootSettled) return;
    const reason = (event as any)?.reason;
    const msg = typeof reason === 'string' ? reason : (reason && reason.message) || JSON.stringify(reason) || 'Unhandled promise rejection';
    if (isIgnorableBrowserNoise(msg)) return;
    const stack = (reason && reason.stack) || undefined;
    showBootError(msg, stack);
  });

  if (!shouldSuppressBootFallback()) {
    bootFailureTimer = window.setTimeout(() => {
      if (bootSettled) return;
      showBootError('Workspace initialization timed out.');
    }, BOOT_FAILURE_TIMEOUT_MS) as unknown as number;
  }

  try { (window as any).__TP_MARK_BOOT_SETTLED__ = markBootSettled; } catch {}
} catch {}

try {
  const originalRemoveChild = Node.prototype.removeChild;
  if (!(Node.prototype as any).__ttRemoveChildPatched) {
    (Node.prototype as any).__ttRemoveChildPatched = true;
    Node.prototype.removeChild = function (this: Node, child: Node) {
      try {
        if (child && child.parentNode !== this) {
          return child;
        }
      } catch {}
      return originalRemoveChild.call(this, child);
    } as typeof Node.prototype.removeChild;
  }
} catch {}

// ---------------------------------------------------------------------------
// Marketing-only base hosts (trade-proof.com / www / legacy aliases).
//
// These hostnames must ALWAYS render the public marketing website and must never
// boot the application: no App mount, no auth bootstrap, no tenant bootstrap, and
// no redirect into the workspace. Marketing paths render the PublicSiteShell
// directly; any app path (login/signup/auth/workspace) is redirected to the
// canonical application host (app.trade-proof.com) which preserves the path.
// ---------------------------------------------------------------------------

// Public paths that belong to the application host, never the marketing site.
const MARKETING_REDIRECT_APP_PATHS = new Set<string>([
  '/login',
  '/signup',
  '/auth/callback',
  '/auth/reset-password',
  '/forgot-password',
  '/demo',
]);

const MARKETING_APP_HOST = `https://app.${WHITE_LABEL_BASE_DOMAIN}`;

type MarketingShellPath = Exclude<PublicRoutePath, '/login' | '/signup'>;

function readMarketingRoute(): { path: MarketingShellPath; hash: string | null } | null {
  try {
    const pathname = String(window.location.pathname || '/').trim() || '/';
    const normalized = normalizePublicPath(pathname);
    const hash = String(window.location.hash || '').replace(/^#/, '').trim() || null;
    if (!normalized || MARKETING_REDIRECT_APP_PATHS.has(normalized)) return null;
    return { path: normalized as MarketingShellPath, hash };
  } catch {
    return { path: '/', hash: null };
  }
}

function redirectToMarketingAppHost(nextPath?: PublicRoutePath, options?: { hash?: string | null }) {
  try {
    const pathname = String(nextPath || window.location.pathname || '/').trim() || '/';
    const hash = options?.hash ? `#${options.hash}` : window.location.hash;
    const target = `${MARKETING_APP_HOST}${pathname}${window.location.search}${hash}`;
    window.location.replace(target);
  } catch {}
}

function MarketingOnlySite() {
  const initial = (() => {
    try {
      return readMarketingRoute();
    } catch {
      return { path: '/' as MarketingShellPath, hash: null };
    }
  })();
  const [route, setRoute] = useState<{ path: MarketingShellPath; hash: string | null }>(
    initial ?? { path: '/', hash: null },
  );
  const redirectToAppRef = useRef(initial === null);

  useEffect(() => {
    try {
      document.getElementById('bootstrap-shell')?.remove();
    } catch {}
    if (redirectToAppRef.current) {
      redirectToMarketingAppHost();
    }
  }, []);

  const navigate = useCallback((nextPath: PublicRoutePath, options?: { hash?: string | null }) => {
    if (MARKETING_REDIRECT_APP_PATHS.has(nextPath)) {
      redirectToMarketingAppHost(nextPath, options);
      return;
    }
    const nextHash = options?.hash ? `#${options.hash}` : '';
    try {
      const nextUrl = `${nextPath}${nextHash}`;
      if (`${window.location.pathname}${window.location.hash}` !== nextUrl) {
        window.history.pushState({}, '', nextUrl);
      }
    } catch {}
    setRoute({ path: nextPath as MarketingShellPath, hash: options?.hash || null });
    try { window.scrollTo?.({ top: 0, left: 0, behavior: 'auto' }); } catch {}
  }, []);

  if (redirectToAppRef.current) return null;
  return <PublicSiteShell path={route.path} hash={route.hash} onNavigate={navigate} />;
}

try {
  if (!rootEl) {
    showBootError('Missing #root element');
  } else {
    if (maybeHandleSnapTradeCallback()) {
      // The callback page only needs to notify the opener and close.
      try { (window as any).__TP_MARK_BOOT_SETTLED__?.(); } catch { }
    } else {
      // Marketing-only hosts never boot the application (no auth, no tenant
      // bootstrap, no workspace redirect). They render the public site directly.
      const bootHostname = String(window.location.hostname || '').trim().toLowerCase();
      const marketingOnlyHost = isTradeProofMarketingOnlyHostname(bootHostname);

      if (marketingOnlyHost) {
        createRoot(rootEl).render(<MarketingOnlySite />);
      } else {
        // Attach dev-only debug helpers for tick-size validation
        attachDebugHelpers();
        createRoot(rootEl).render(
          <ErrorBoundary>
            <RuntimeTenantGate>
              <DisplayTimezoneProvider>
                <App />
              </DisplayTimezoneProvider>
            </RuntimeTenantGate>
          </ErrorBoundary>,
        );
      }
      window.setTimeout(() => {
        try { (window as any).__TP_MARK_BOOT_SETTLED__?.(); } catch { }
      }, 0);
    }
  }
} catch (err: any) {
  showBootError(err?.message || 'Unknown render error');
}
